import {
  Loader,
  Message,
  Modal,
  SelectPicker,
  Uploader,
  useToaster,
} from "rsuite";
import { getPurchasingConditionReportDataForExcel } from "../../react-query/hooks/HVendorQuoteAssessment";
import JSZip from "jszip";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { appInsights } from "../../router/router";

interface IPurchasingConditionDownloadModalProps {
  openDownloadModal: boolean;
  setOpenDownloadModal: (value: boolean) => void;
  negotiationMonth?: string | undefined;
  commodity: string | null;
  vendorList: { vendor: string }[];
  baseSku: string | null;
}

const PurchasingConditionDownloadModal = ({
  openDownloadModal,
  setOpenDownloadModal,
  negotiationMonth,
  commodity,
  vendorList,
  baseSku,
}: IPurchasingConditionDownloadModalProps) => {
  const handleDownloadExcel = async (vendor: string) => {
    const excelData = await getPurchasingConditionReportDataForExcel(
      localStorage.getItem("userEmail"),
      commodity,
      "QuoteUpload",
      vendor,
      baseSku,
    );
    let newdata = excelData?.data?.data;
    let currentdate = new Date();

    // Add prefix mapping for commodity
    const prefixPurc =
      commodity === "Steel Conduit"
        ? "TUBPA"
        : commodity === "PVC Conduit"
          ? "PVCPA"
          : commodity === "Copper Wire"
            ? "WYRPAAT"
            : null;

    if (newdata) {
      let zip = new JSZip();

      const allWorkbook = new ExcelJS.Workbook();
      const Headers = [
        { header: "ConditionType", key: "ConditionType", width: 15 },
        { header: "SupplierDCI", key: "VendorDCI", width: 15 },
        { header: "LocationType", key: "LocationType", width: 15 },
        { header: "Location", key: "Location", width: 15 },
        { header: "UPCNumber", key: "UPCNumber", width: 15 },
        { header: "Catlog", key: "Catlog", width: 15 },
        { header: "Price/Rate", key: "Price/Rate", width: 15 },
        { header: "UOM", key: "UOM", width: 15 },
        { header: "PrefixPurc", key: "PrefixPurc", width: 15 },
        { header: "ProductGroup", key: "ProductGroup", width: 15 },
        { header: "PriceGroup", key: "PriceGroup", width: 15 },
        { header: "DISC CODE", key: "DiscCode", width: 15 },
        { header: "SupplierID", key: "VendorID", width: 15 },
        { header: "EffectiveDate", key: "EffectiveDate", width: 15 },
        { header: "ExpirationDate", key: "ExpirationDate", width: 15 },
        {
          header: "PublishedEffectiveDate",
          key: "PublishedEffectiveDate",
          width: 10,
        },
        {
          header: "PublishedExpirationDate",
          key: "PublishedExpirationDate",
          width: 10,
        },
      ];
      const allWorkSheet = allWorkbook.addWorksheet(
        `All_Districts_${vendor}_sheet`,
      );
      allWorkSheet.columns = Headers;
      const filePromises = Object?.keys(newdata)?.map(
        async (item: any, index, arr) => {
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet(
            `${newdata[item][0].Location}_${newdata[item][0].VendorName}_sheet`,
          );

          worksheet.columns = Headers;
          const formattedData = newdata[item].map((ele: any) => ({
            ConditionType: ele.ConditionType,
            VendorDCI: ele.VendorDCI,
            LocationType: ele.LocationType,
            Location: ele.Location,
            UPCNumber: ele.UPCNumber,
            Catlog: ele.CATLOG,
            "Price/Rate": Number(ele["Price/Rate"]),
            UOM: ele.UOM,
            PrefixPurc: ele.PrefixPurc,
            ProductGroup: ele.ProductGroup,
            PriceGroup: ele.PriceGroup,
            DiscCode: ele.DiscCode,
            VendorID: ele.VendorID,
            EffectiveDate: ele.EffectiveDate,
            ExpirationDate: ele.ExpirationDate,
            PublishedEffectiveDate: ele.PublishedEffectiveDate,
            PublishedExpirationDate: ele.PublishedExpirationDate,
          }));
          worksheet.addRows(formattedData);

          allWorkSheet.addRows(formattedData);

          // Apply styling to all data rows
          worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            row.eachCell({ includeEmpty: true }, (cell) => {
              cell.alignment = {
                // wrapText: true,
                vertical: "middle",
                horizontal: rowNumber <= 1 ? "center" : "left",
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
              if (rowNumber <= 1) {
                cell.font = { bold: true };
                row.height = 30;
              }
            });
          });

          worksheet.columns.forEach((column: any) => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell: any) => {
              if (cell.value) {
                const cellValue = cell.value.toString();
                maxLength = Math.max(maxLength, cellValue.length);
              }
            });
            column.width = maxLength + 2; // Add some padding to the width
          });

          // Apply styling to all data rows for allWorkSheet
          allWorkSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            row.eachCell({ includeEmpty: true }, (cell) => {
              cell.alignment = {
                // wrapText: true,
                vertical: "middle",
                horizontal: rowNumber <= 1 ? "center" : "left",
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
              if (rowNumber <= 1) {
                cell.font = { bold: true };
                row.height = 30;
              }
            });
          });

          allWorkSheet.columns.forEach((column: any) => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell: any) => {
              if (cell.value) {
                const cellValue = cell.value.toString();
                maxLength = Math.max(maxLength, cellValue.length);
              }
            });
            column.width = maxLength + 2; // Add some padding to the width
          });

          const buffer = await workbook.xlsx.writeBuffer();
          const excelBlob1 = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          zip.file(
            `${newdata[item][0].Location}_ZRP_${prefixPurc ?? "PVCPA"} PC ${newdata[item][0].VendorName}PA Commodity Desk ${currentdate.getMonth() + 1
            }-${currentdate.getDate()}-${currentdate
              .getFullYear()
              .toString()
              .slice(2)}.xlsx`,
            excelBlob1,
          );
          if (index === Object?.keys(newdata).length - 1) {
            const buffer = await allWorkbook.xlsx.writeBuffer();
            const excelBlob1 = new Blob([buffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            zip.file(
              `All_Districts_ZRP_${prefixPurc ?? "PVCPA"} PC ${newdata[item][0].VendorName}PA Commodity Desk ${currentdate.getMonth() + 1
              }-${currentdate.getDate()}-${currentdate
                .getFullYear()
                .toString()
                .slice(2)}.xlsx`,
              excelBlob1,
            );
          }
        },
      );

      // Wait for all promises to complete before generating the zip
      await Promise.all(filePromises);
      // Generate and save the zip file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(
        zipBlob,
        `${newdata[Object.keys(newdata)[0]][0].VendorName
        }_PurchasingConditionReport.zip`,
      );
      appInsights.trackEvent({
        name: "Supplier Quote Assessment: Purchasing Conditions",
        properties: {
          description: `${localStorage.getItem("userEmail")} has downloaded "${newdata[Object.keys(newdata)[0]][0].VendorName
            }_PurchasingConditionReport.zip" File`,
        },
      });
    }
  };

  return (
    <div className="flex justify-center items-center">
      <Modal
        className="w-[30vw] purchasing-condition-modal"
        open={openDownloadModal}
        onClose={() => {
          setOpenDownloadModal(false);
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title className="font-bold text-[1.05vw]">
            {"Download Purchasing Condition"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col justify-between items-center gap-1.5 pt-[0vh] pb-[2vh]">
            {negotiationMonth && negotiationMonth?.length !== 0 && (
              <div className="flex justify-start items-center w-full px-[1vh] pb-[2.5vh]">
                <span className="text-[0.9vw] font-semibold">
                  Download Report For:
                </span>
                <span className="text-[0.9vw] font-semibold pl-[0.75vh] underline text-deloitteGreen">
                  {negotiationMonth}
                </span>
              </div>
            )}
            {vendorList && vendorList.length > 0 && (
              <div className="flex flex-col w-full">
                <label className="font-bold text-[1vw] px-[1vh]">
                  Supplier List
                </label>
                <div className="flex flex-col justify-center items-center gap-2 w-full border-2 px-[0.5vw] py-[0.5vh] border-t-[0.5vh] border-t-deloitteGreen rounded-sm">
                  {Object.values(vendorList).map((vendorObj) => {
                    return (
                      <div className="w-full flex justify-between items-center">
                        <label className="font-medium text-[1vw]">
                          {vendorObj.vendor}
                        </label>
                        <button
                          onClick={() => handleDownloadExcel(vendorObj.vendor)}
                          className={`text-[1vw] font-semibold bg-deloitteGreen rounded-md text-white px-[0.8vw] py-[0.8vh]`}
                        // disabled={!allowAssessment}
                        >
                          Download
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PurchasingConditionDownloadModal;
